import * as Identity from '@ingrooves/identity-web';
import { AuthResult } from '@ingrooves/identity-web';
import singleton from 'singleton';
import store from '../state/store';
import { addAuthValues, clearAuthValues, IAuth } from '../state/actions/auth';
import {
  Internal,
  ProjRead,
  ProjUiAccess,
  ProjWrite,
  Write,
} from '../constants/AuthPermission';
import { logger } from '../utils/Logger';

class AuthMiddleware extends singleton {
  private static readonly PERMISSIONS_CLAIM =
    'https://identity.ingrooves.com/claims/permissions';
  private static readonly USER_METADATA_CLAIM =
    'https://identity.ingrooves.com/claims/user_metadata';

  private client!: Identity.IdentityClient;
  private defaultLogoutPath = '/logout';

  setConfig(
    domain: any,
    clientID: any,
    audience: any,
    projectServiceUrl: string,
    projectServiceClientId: string,
    projectServiceAudience: string,
    menuConfigAudience: any,
    menuUrl: any,
    userMetaData: any
  ) {
    this.client = new Identity.IdentityClient({
      domain: domain,
      clientID: clientID,
      logoutPath: this.defaultLogoutPath,
    });
    this.audience = audience;
    const projectServiceClient = new Identity.IdentityClient({
      domain: domain,
      clientID: projectServiceClientId,
    });
    this.projectServiceConfig = {
      audience: projectServiceAudience,
      client: projectServiceClient,
      baseUrl: projectServiceUrl,
    };
    this.externalApiConfigs = [this.projectServiceConfig];
    this.menuConfig = {
      audience: menuConfigAudience,
      url: menuUrl,
      userMetaData: userMetaData,
    };
  }

  setHeader(token: any) {
    return {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
  }

  // Clean up user store information and execute client logout
  async userLogout() {
    store.dispatch(clearAuthValues());
    return await this.client.logout();
  }

  async getUpdatedLanguage(language: string) {
    const body = JSON.stringify({ language: language });
    try {
      const menuConfig = await this.client.renew(this.menuConfig.audience, {
        scope: 'openid profile email',
        responseType: 'token id_token',
      });
      return await fetch(this.menuConfig.userMetaData, {
        credentials: 'include',
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${menuConfig.accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: body,
      });
    } catch (error) {
      logger.error(error);
    }
  }
  checkExpiry = (expiryDate?: Date) => {
    const currentDate = new Date();
    let isExpired = true;
    if (expiryDate !== undefined) {
      isExpired = currentDate.getTime() > expiryDate.getTime();
    }
    return isExpired;
  };

  /**
   * Renew PRS session
   * if the path is /logout the renew client service will flush the auth data.
   */
  async renewClientSession(forceAuthorize = false): Promise<AuthResult> {
    return await this.client.renew(this.audience, {
      scope: 'openid profile email',
      responseType: 'token id_token',
      forceAuthorize,
    });
  }

  async refreshTokens(forceAuthorize = false) {
    // renews PRS session
    const prsSession = await this.renewClientSession(forceAuthorize);
    // If the prs session is null the user will redirect to logout
    if (!prsSession) await this.client.logout(this.defaultLogoutPath);

    // Set PRS permissions
    const prsPermissions =
      prsSession?.idToken[AuthMiddleware.PERMISSIONS_CLAIM];
    if (prsPermissions.indexOf('prs:read_access') < 0) {
      return await this.client.logout(this.defaultLogoutPath);
    }

    // get Project Services permissions
    const projPermissions = await this.projectServiceConfig.client.renew(
      this.projectServiceConfig.audience,
      {
        scope: 'openid profile email',
        responseType: 'token id_token',
      }
    );
    const projPerms =
      projPermissions?.idToken[AuthMiddleware.PERMISSIONS_CLAIM];
    const sub = prsSession?.idToken['sub'];

    const userLanguage =
      prsSession?.idToken[AuthMiddleware.USER_METADATA_CLAIM]?.language;
    this.setAuth(prsSession, prsPermissions, projPerms, sub, userLanguage);
  }

  async getTokenAsync(baseUrl?: string) {
    const externalApiConfig = this.externalApiConfigs
      .filter((eac: any) => eac.baseUrl === baseUrl)
      ?.at(0);
    const apiClient = externalApiConfig?.client ?? this.client;
    const apiAudience = externalApiConfig?.audience ?? this.audience;
    const response = await apiClient.renew(apiAudience, {
      scope: 'openid profile email',
      responseType: 'token id_token',
    });
    return response?.accessToken;
  }

  private setAuth(
    prsPermissions: any,
    prsPerms: any,
    projectServicePerms: any,
    sub: string,
    userLanguage?: string
  ) {
    const authStore: IAuth = store.getState().auth;
    if (!authStore.loggedIn && this.checkExpiry(authStore.expiresOn)) {
      const auth1: IAuth = {
        sub,
        authToken: prsPermissions.accessToken,
        expiresOn: new Date(Number(prsPermissions.expiresAt)),
        loggedIn: true,
        permissions: prsPerms,
        hasWriteAccess:
          prsPerms.find((item: any) => item === Write) !== undefined,
        hasInternalAccess:
          prsPerms.find((item: any) => item === Internal) !== undefined,
        hasProjectServiceRead:
          projectServicePerms.find((item: any) => item === ProjRead) !==
          undefined,
        hasProjectServiceWrite:
          projectServicePerms.find((item: any) => item === ProjWrite) !==
          undefined,
        hasProjectServiceUiAccess:
          projectServicePerms.find((item: any) => item === ProjUiAccess) !==
          undefined,
        userLanguage,
      };
      store.dispatch(addAuthValues(auth1));
    }
  }

  async getMenuConfigAudienceAsync(forceAuthorize = false) {
    try {
      const menuConfig = await this.client.renew(this.menuConfig.audience, {
        scope: 'openid profile email',
        responseType: 'token id_token',
        forceAuthorize,
      });
      const fetchMenu = await fetch(this.menuConfig.url, {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${menuConfig.accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      return fetchMenu.status === 401
        ? await this.client.logout(this.defaultLogoutPath)
        : fetchMenu.json();
    } catch (error) {
      logger.error(error);
    }
  }
}
export default AuthMiddleware.get();
